/**
 * Phone Number Validation Utilities
 *
 * This module provides comprehensive phone number validation functionality
 * with international support for the BiteWise provider registration system.
 * Supports country-specific formatting and validation rules.
 *
 * @module utils/validation/phoneValidation
 */

import type { ValidationResult } from '@/types/registration';
import type { PhoneNumber, Country } from '@/types/provider';

/**
 * Phone validation configuration
 */
const PHONE_CONFIG = {
  /** Minimum phone number length (digits only) */
  MIN_LENGTH: 8,
  /** Maximum phone number length (digits only) */
  MAX_LENGTH: 15,
  /** Default country code */
  DEFAULT_COUNTRY: 'AU'
} as const;

/**
 * Phone validation error messages
 */
const PHONE_ERRORS = {
  REQUIRED: 'Phone number is required',
  INVALID_FORMAT: 'Please enter a valid phone number',
  TOO_SHORT: 'Phone number is too short',
  TOO_LONG: 'Phone number is too long',
  INVALID_COUNTRY_CODE: 'Invalid country code',
  INVALID_CHARACTERS: 'Phone number contains invalid characters',
  MISSING_AREA_CODE: 'Please include the area code'
} as const;

/**
 * Country-specific phone validation patterns and formatting rules
 */
const COUNTRY_PHONE_RULES: Record<string, {
  pattern: RegExp;
  minLength: number;
  maxLength: number;
  format: (number: string) => string;
  example: string;
}> = {
  AU: {
    pattern: /^(?:\+61|61|0)?[1-9]\d{8}$/,
    minLength: 9,
    maxLength: 10,
    format: (number: string) => {
      const cleaned = number.replace(/\D/g, '');
      // Match Australian format: 0X XXXX XXXX where first X is 1-9
      const match = cleaned.match(/^(?:61|0)?([1-9]\d{8})$/);

      if (match) {
        const nationalNumber = match[1];
        return `0${nationalNumber.slice(0, 1)} ${nationalNumber.slice(1, 5)} ${nationalNumber.slice(5)}`;
      }
      return cleaned;
    },
    example: '01 2345 6789'
  },
  US: {
    pattern: /^(?:\+1|1)?[2-9]\d{2}\d{3}\d{4}$/,
    minLength: 10,
    maxLength: 10,
    format: (number: string) => {
      const cleaned = number.replace(/\D/g, '');
      const match = cleaned.match(/^(?:1)?(\d{3})(\d{3})(\d{4})$/);
      return match ? `(${match[1]}) ${match[2]}-${match[3]}` : cleaned;
    },
    example: '(555) 123-4567'
  },
  GB: {
    pattern: /^(?:\+44|44|0)?[1-9]\d{8,9}$/,
    minLength: 10,
    maxLength: 11,
    format: (number: string) => {
      const cleaned = number.replace(/\D/g, '');
      const mobile = cleaned.match(/^(?:44|0)?(7\d{9})$/);
      const landline = cleaned.match(/^(?:44|0)?([1-9]\d{8,9})$/);

      if (mobile) {
        return `${mobile[1].slice(0, 5)} ${mobile[1].slice(5)}`;
      } else if (landline) {
        return `${landline[1].slice(0, 4)} ${landline[1].slice(4)}`;
      }
      return cleaned;
    },
    example: '07700 900123'
  },
  CA: {
    pattern: /^(?:\+1|1)?[2-9]\d{2}\d{3}\d{4}$/,
    minLength: 10,
    maxLength: 10,
    format: (number: string) => {
      const cleaned = number.replace(/\D/g, '');
      const match = cleaned.match(/^(?:1)?(\d{3})(\d{3})(\d{4})$/);
      return match ? `(${match[1]}) ${match[2]}-${match[3]}` : cleaned;
    },
    example: '(416) 555-0123'
  }
};

/**
 * Common country data for phone number selection
 */
export const COMMON_COUNTRIES: Country[] = [
  { name: 'Australia', code: 'AU', dialCode: '+61', flag: '🇦🇺' },
  { name: 'United States', code: 'US', dialCode: '+1', flag: '🇺🇸' },
  { name: 'United Kingdom', code: 'GB', dialCode: '+44', flag: '🇬🇧' },
  { name: 'Canada', code: 'CA', dialCode: '+1', flag: '🇨🇦' },
  { name: 'New Zealand', code: 'NZ', dialCode: '+64', flag: '🇳🇿' },
  { name: 'Germany', code: 'DE', dialCode: '+49', flag: '🇩🇪' },
  { name: 'France', code: 'FR', dialCode: '+33', flag: '🇫🇷' },
  { name: 'Japan', code: 'JP', dialCode: '+81', flag: '🇯🇵' },
  { name: 'Singapore', code: 'SG', dialCode: '+65', flag: '🇸🇬' },
  { name: 'India', code: 'IN', dialCode: '+91', flag: '🇮🇳' }
];

/**
 * Validates phone number according to international standards and country-specific rules
 *
 * @param phone - The phone number object to validate
 * @returns ValidationResult with isValid flag and error message if invalid
 *
 * @example
 * ```typescript
 * const result = validatePhoneNumber({
 *   countryCode: '+61',
 *   isoCode: 'AU',
 *   number: '0412345678',
 *   formatted: '0412 345 678',
 *   international: '+61412345678'
 * });
 *
 * if (result.isValid) {
 *   logger.log('Phone number is valid');
 * } else {
 *   logger.log('Error:', result.error);
 * }
 * ```
 */
export const validatePhoneNumber = (phone: PhoneNumber): ValidationResult => {
  // Check if phone number is provided
  if (!phone.number || phone.number.trim().length === 0) {
    return {
      isValid: false,
      error: PHONE_ERRORS.REQUIRED
    };
  }

  // Check if country code is provided
  if (!phone.isoCode || !phone.countryCode) {
    return {
      isValid: false,
      error: PHONE_ERRORS.INVALID_COUNTRY_CODE
    };
  }

  const cleanedNumber = phone.number.replace(/\D/g, '');

  // Basic length validation
  if (cleanedNumber.length < PHONE_CONFIG.MIN_LENGTH) {
    return {
      isValid: false,
      error: PHONE_ERRORS.TOO_SHORT
    };
  }

  if (cleanedNumber.length > PHONE_CONFIG.MAX_LENGTH) {
    return {
      isValid: false,
      error: PHONE_ERRORS.TOO_LONG
    };
  }

  // Country-specific validation
  const countryRule = COUNTRY_PHONE_RULES[phone.isoCode];
  if (countryRule) {
    return validateCountrySpecificPhone(phone.number, phone.isoCode, countryRule);
  }

  // Generic international validation for countries without specific rules
  return validateGenericPhone(cleanedNumber);
};

/**
 * Validates phone number against country-specific rules
 *
 * @param phoneNumber - The phone number to validate
 * @param countryCode - ISO country code
 * @param rule - Country-specific validation rule
 * @returns ValidationResult
 */
const validateCountrySpecificPhone = (
  phoneNumber: string,
  countryCode: string,
  rule: typeof COUNTRY_PHONE_RULES[string]
): ValidationResult => {
  const cleanedNumber = phoneNumber.replace(/\D/g, '');

  // Check length constraints
  if (cleanedNumber.length < rule.minLength) {
    return {
      isValid: false,
      error: `${PHONE_ERRORS.TOO_SHORT} (minimum ${rule.minLength} digits for ${countryCode})`
    };
  }

  if (cleanedNumber.length > rule.maxLength) {
    return {
      isValid: false,
      error: `${PHONE_ERRORS.TOO_LONG} (maximum ${rule.maxLength} digits for ${countryCode})`
    };
  }

  // Check pattern
  if (!rule.pattern.test(phoneNumber.replace(/\s/g, ''))) {
    return {
      isValid: false,
      error: `${PHONE_ERRORS.INVALID_FORMAT}. Example: ${rule.example}`
    };
  }

  return { isValid: true };
};

/**
 * Generic phone validation for countries without specific rules
 *
 * @param cleanedNumber - Phone number with only digits
 * @returns ValidationResult
 */
const validateGenericPhone = (cleanedNumber: string): ValidationResult => {
  // Basic format validation
  if (!/^\d+$/.test(cleanedNumber)) {
    return {
      isValid: false,
      error: PHONE_ERRORS.INVALID_CHARACTERS
    };
  }

  return { isValid: true };
};

/**
 * Formats phone number according to country-specific rules
 *
 * @param phoneNumber - Raw phone number
 * @param countryCode - ISO country code
 * @returns Formatted phone number
 *
 * @example
 * ```typescript
 * const formatted = formatPhoneNumber('0412345678', 'AU');
 * logger.log(formatted); // "0412 345 678"
 * ```
 */
export const formatPhoneNumber = (phoneNumber: string, countryCode: string): string => {
  const rule = COUNTRY_PHONE_RULES[countryCode];

  if (rule) {
    return rule.format(phoneNumber);
  }

  // Generic formatting for unknown countries
  const cleaned = phoneNumber.replace(/\D/g, '');
  if (cleaned.length <= 4) return cleaned;
  if (cleaned.length <= 7) return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
  return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
};

/**
 * Converts phone number to international format
 *
 * @param phoneNumber - National phone number
 * @param countryCode - ISO country code
 * @param dialCode - International dial code
 * @returns International format phone number
 *
 * @example
 * ```typescript
 * const international = toInternationalFormat('0412345678', 'AU', '+61');
 * logger.log(international); // "+61412345678"
 * ```
 */
export const toInternationalFormat = (
  phoneNumber: string,
  countryCode: string,
  dialCode: string
): string => {
  let cleaned = phoneNumber.replace(/\D/g, '');

  // Remove leading zeros for most countries
  if (countryCode !== 'IT' && cleaned.startsWith('0')) {
    cleaned = cleaned.substring(1);
  }

  // Remove country code if already present
  const dialCodeDigits = dialCode.replace(/\D/g, '');
  if (cleaned.startsWith(dialCodeDigits)) {
    cleaned = cleaned.substring(dialCodeDigits.length);
  }

  return `${dialCode}${cleaned}`;
};

/**
 * Parses phone number input and extracts components
 *
 * @param input - Raw phone number input
 * @param defaultCountry - Default country code to use
 * @returns Parsed phone number components
 */
export const parsePhoneNumber = (
  input: string,
  defaultCountry: string = PHONE_CONFIG.DEFAULT_COUNTRY
): Partial<PhoneNumber> => {
  const cleaned = input.replace(/\D/g, '');

  // Try to detect country from international format
  for (const country of COMMON_COUNTRIES) {
    const dialCodeDigits = country.dialCode.replace(/\D/g, '');
    if (cleaned.startsWith(dialCodeDigits)) {
      const nationalNumber = cleaned.substring(dialCodeDigits.length);
      return {
        countryCode: country.dialCode,
        isoCode: country.code,
        number: nationalNumber,
        formatted: formatPhoneNumber(nationalNumber, country.code),
        international: `${country.dialCode}${nationalNumber}`
      };
    }
  }

  // Use default country
  const defaultCountryData = COMMON_COUNTRIES.find(c => c.code === defaultCountry);
  if (defaultCountryData) {
    return {
      countryCode: defaultCountryData.dialCode,
      isoCode: defaultCountryData.code,
      number: cleaned,
      formatted: formatPhoneNumber(cleaned, defaultCountry),
      international: toInternationalFormat(cleaned, defaultCountry, defaultCountryData.dialCode)
    };
  }

  return {
    number: cleaned
  };
};

/**
 * Gets example phone number for a country
 *
 * @param countryCode - ISO country code
 * @returns Example phone number
 */
export const getPhoneExample = (countryCode: string): string => {
  const rule = COUNTRY_PHONE_RULES[countryCode];
  return rule?.example || '123 456 7890';
};

/**
 * Validates if a country code is supported
 *
 * @param countryCode - ISO country code to validate
 * @returns Whether the country is supported
 */
export const isSupportedCountry = (countryCode: string): boolean => {
  return COMMON_COUNTRIES.some(country => country.code === countryCode);
};

/**
 * Gets country information by ISO code
 *
 * @param countryCode - ISO country code
 * @returns Country information or undefined
 */
export const getCountryByCode = (countryCode: string): Country | undefined => {
  return COMMON_COUNTRIES.find(country => country.code === countryCode);
};

/**
 * Searches countries by name or dial code
 *
 * @param query - Search query
 * @returns Matching countries
 */
export const searchCountries = (query: string): Country[] => {
  const lowerQuery = query.toLowerCase();
  return COMMON_COUNTRIES.filter(country =>
    country.name.toLowerCase().includes(lowerQuery) ||
    country.dialCode.includes(query) ||
    country.code.toLowerCase().includes(lowerQuery)
  );
};